import _ from 'lodash';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Button,
  Checkbox, Item, Popup, Segment, TextArea
} from 'semantic-ui-react';
import uniqid from 'uniqid';
import { endpoint } from '../../api/http';
import { ReservationPolicy as ReservationPolicyAPI } from '../../api/services/next';
import CancellationAndDepositPoliciesPopup from '../../components/CancellationAndDepositPoliciesPopup';
import ReservationPolicyUpdatePopup from '../../components/ReservationPolicyUpdatePopup';
import { EntityType, Languages, MenuActions } from '../../components/shared/constants';
import { EditButton, IconButton } from '../../components/shared/icons';
import { onError, onSuccess, onWarning } from '../../components/shared/messages';
import NumericInput from '../../components/shared/NumericInput';
import SideBar from '../../components/shared/SideBar';
import TextInput from '../../components/shared/TextInput';
import { buildAutoDescriptionForPolicy } from '../../components/shared/reservationPolicy';
import { useCurrentHotel } from '../../hooks/useCurrentHotel';
import { useLoader } from '../../hooks/useLoader';
import { useTextResources, useMessageResources } from '../../language';
import './style.css';
const policyPropertiesEnum = {
  Name: 'Name',
  Description: 'Description',
  Priority: 'Priority',
  IsActive: 'IsActive',
};

const ReservationPolicy = () => {
  const text = useTextResources();
  const message = useMessageResources();
  const currentHotel = useCurrentHotel();

  const hotelTimezone = currentHotel?.HotelParameter?.TimeZone;
  const [reservationPolicies, setReservationPolicies] = useState([]);
  const [filteredReservationPolicies, setFilteredReservationPolicies] =
    useState([]);
  const [
    showCancellationAndDepositPoliciesPopup,
    setShowCancellationAndDepositPoliciesPopup,
  ] = useState(false);
  const [chosenReservationPolicy, setChosenReservationPolicy] = useState({});
  const [
    reservationPolicyUpdatePopupVisible,
    setReservationPolicyUpdatePopupVisible,
  ] = useState(true);

  const [showOnlyActive, setShowOnlyActive] = useState(true)

  const loader = useLoader();

  const dragUiRef = useRef({ dragOverEl: null, indicatorEl: null });

  const clearDragUi = useCallback(() => {
    const { dragOverEl, indicatorEl } = dragUiRef.current;
    if (dragOverEl) {
      dragOverEl.classList.remove('drag-over');
    }
    if (indicatorEl) {
      indicatorEl.remove();
    }
    dragUiRef.current.dragOverEl = null;
    dragUiRef.current.indicatorEl = null;
  }, []);

  const policyKey = useCallback((policy) => (policy?.Id ? policy.Id : policy?.InternalId), []);

  useEffect(() => {
    if (window.optima?.commands) {
      window.optima.commands.unsubscribe(MenuActions.toggle);
      window.optima.commands.subscribe(MenuActions.toggle, () => {
        setReservationPolicyUpdatePopupVisible((prev) => !prev);
      });
    }
  }, [reservationPolicyUpdatePopupVisible]);

  useEffect(() => {
    loader(true);
    (async () => {
      try {
        const userLang = window.optima?.user?.UserSettings[0]?.LanguageID || Languages.English.ID;
        let result = await ReservationPolicyAPI.GetReservationPolicies({
          LanguageId: userLang,
          InActive: true,
        });
        setReservationPolicies(orderReservationPolicies(result));
      } catch (error) {
        onError(error);
      } finally {
        loader(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (window?.optima?.commands) {
      window.optima.commands.unSubscribeAll(MenuActions.save);
      window.optima.commands.subscribe(MenuActions.save, save);
    }
  }, [reservationPolicies]);

  useEffect(() => {
    if (showOnlyActive) {
      setFilteredReservationPolicies(reservationPolicies.filter(r => r.IsActive))
    } else {
      setFilteredReservationPolicies(reservationPolicies)
    }
  }, [showOnlyActive, reservationPolicies])

  const orderReservationPolicies = useCallback((reservationPolicies) => {
    return _(reservationPolicies)
      .orderBy(['Priority', 'Name'], ['asc', 'asc'])
      .value()
  }, [])

  const validateReservationPolicies = useCallback((policies) => {
    // Check for empty names
    const policiesWithEmptyNames = policies.filter(policy => 
      !policy.Name || !policy.Name.trim()
    );

    if (policiesWithEmptyNames.length > 0) {
      onWarning(message('ReservationPolicyNameRequired'));
      return false;
    }

    // Check for names that exceed maximum length (50 characters)
    const policiesWithLongNames = policies.filter(policy => 
      policy.Name && policy.Name.trim().length > 50
    );

    if (policiesWithLongNames.length > 0) {
      onWarning(message('ReservationPolicyNameTooLong'));
      return false;
    }

    // Check for duplicate names
    const policyNames = policies
      .filter(policy => policy.IsActive === true && policy.Name && policy.Name.trim())
      .map(policy => policy.Name.toLowerCase());

    const duplicateNames = policyNames.filter((name, index) =>
      policyNames.indexOf(name) !== index
    );

    if (duplicateNames.length > 0) {
      onWarning(message('ReservationPolicyNameExists'));
      return false;
    }

    // Check for duplicate priorities (only for active policies with valid priorities)
    // Priority 0 is NOT special; it must be unique like any other priority.
    const activePoliciesWithPriority = policies.filter(policy => 
      policy.IsActive === true &&
      policy.Priority !== null && 
        policy.Priority !== undefined &&
        policy.Priority >= 0
    );

    const priorityValues = activePoliciesWithPriority.map(policy => policy.Priority);
    const duplicatePriorities = priorityValues.filter((priority, index) =>
      priorityValues.indexOf(priority) !== index
    );

    if (duplicatePriorities.length > 0) {
      onWarning(message('ReservationPolicyPriorityExists') );
      return false;
    }

    // Check for missing priorities (required for active policies)
    const activePoliciesWithMissingPriority = policies.filter(policy =>
      policy.IsActive === true &&
      (policy.Priority === null || typeof policy.Priority === 'undefined' || policy.Priority === '')
    );

    if (activePoliciesWithMissingPriority.length > 0) {
      onWarning(message('RequiredField').replace('{0}', text(policyPropertiesEnum.Priority)).replace(':', ''));
      return false;
    }

    return true;
  }, [message, text, onWarning]);

  const save = async () => {
    // Validate policies before saving
    if (!validateReservationPolicies(reservationPolicies)) {
      return;
    }

    loader(true);
    try {
      if (reservationPolicies?.length) {
        let result = await ReservationPolicyAPI.AddReservationPolicies(reservationPolicies);
        setReservationPolicies(orderReservationPolicies(result));
        onSuccess();
      }
    } catch (error) {
      onError(error);
    } finally {
      loader(false);
    }
  };

  const onAddNewPolicy = () => {
    const newPolicy = {
      Id: 0,
      InternalId: uniqid('newPolicy-'),
      Name: '',
      Description: '',
      IsActive: true,
      Priority: null,
    };
    setReservationPolicies((prev) => [newPolicy, ...prev]);
  };

  const onUpdatePolicy = ({ value, property, currentPolicy }) => {
    // Check for duplicate names when updating the Name property
    if (property === policyPropertiesEnum.Name && value) {
      const isDuplicate = reservationPolicies.some(policy =>
        policy.IsActive === true &&
        policy.Name &&
        policy.Name.toLowerCase() === value.toLowerCase() &&
        (policy.Id !== currentPolicy.Id || policy.InternalId !== currentPolicy.InternalId)
      );

      if (isDuplicate) {
        onWarning(message('ReservationPolicyNameExists') );
        return; // Don't update if duplicate
      }
    }

    // Check for duplicate priorities when updating the Priority property (only among active policies)
    // Priorities must be non-negative and unique among active policies.
    if (property === policyPropertiesEnum.Priority && value != null && value >= 0) {
      const isDuplicatePriority = reservationPolicies.some(policy =>
        policy.Priority === value &&
        policy.IsActive === true &&
        (policy.Id !== currentPolicy.Id || policy.InternalId !== currentPolicy.InternalId)
      );

      if (isDuplicatePriority) {
        onWarning(message('ReservationPolicyPriorityExists') );
        // Warn the user about the conflicting priority, but continue updating so they can resolve conflicts later.
      }
    }

    // Check for priority conflicts when reactivating a policy
    if (property === policyPropertiesEnum.IsActive && value === true) {
        // Check if this policy being reactivated has a priority that conflicts with existing active policies
        if (currentPolicy.Priority != null && currentPolicy.Priority >= 0) {
        const isDuplicatePriority = reservationPolicies.some(policy =>
          policy.Priority === currentPolicy.Priority &&
          policy.IsActive === true &&
          (policy.Id !== currentPolicy.Id || policy.InternalId !== currentPolicy.InternalId)
        );

        if (isDuplicatePriority) {
           onWarning(message('ReservationPolicyPriorityExists'));
        }
      }
    }

    setReservationPolicies((prev) =>
      prev.map((prevPolicy) =>
        prevPolicy.Id == currentPolicy.Id && //Policy that was fetched from the server
          prevPolicy.InternalId == currentPolicy.InternalId
          ? //is a new policy - if old policy will be undefined == undefined is true
          { ...currentPolicy, [property]: value }
          : prevPolicy
      )
    );
  };

  // Drag and drop handlers
  const handleDragStart = (e, policy) => {
    if (!policyKey(policy)) {
      e.preventDefault?.();
      return;
    }
    e.dataTransfer.setData('text/plain', JSON.stringify(policy));
    e.dataTransfer.effectAllowed = 'move';
    e.currentTarget.classList.add('dragging');
  };

  const handleDragEnd = (e) => {
    e.currentTarget.classList.remove('dragging');
    clearDragUi();
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDragEnter = (e, policy) => {
    e.preventDefault();
    if (policyKey(policy)) {
      const prevDragOverEl = dragUiRef.current.dragOverEl;
      const prevIndicatorEl = dragUiRef.current.indicatorEl;

      if (prevDragOverEl && prevDragOverEl !== e.currentTarget) {
        prevDragOverEl.classList.remove('drag-over');
      }
      if (prevIndicatorEl) {
        prevIndicatorEl.remove();
      }

      e.currentTarget.classList.add('drag-over');

      // Add visual indicator for drop position
      const rect = e.currentTarget.getBoundingClientRect();
      const mouseY = e.clientY;
      const itemCenter = rect.top + rect.height / 2;

      // Create drop indicator line
      const indicator = document.createElement('div');
      indicator.className = 'drop-indicator';
      indicator.style.position = 'absolute';
      indicator.style.left = '10px';
      indicator.style.right = '10px';
      indicator.style.height = '2px';
      indicator.style.backgroundColor = '#007bff';
      indicator.style.zIndex = '1000';
      indicator.style.pointerEvents = 'none';
      indicator.style.borderRadius = '1px';

      // Ensure the parent has relative positioning
      if (e.currentTarget.style.position !== 'relative') {
        e.currentTarget.style.position = 'relative';
      }

      if (mouseY < itemCenter) {
        // Drop above this item
        indicator.style.top = '0px';
        e.currentTarget.prepend(indicator);
      } else {
        // Drop below this item
        indicator.style.bottom = '0px';
        e.currentTarget.appendChild(indicator);
      }

      dragUiRef.current.dragOverEl = e.currentTarget;
      dragUiRef.current.indicatorEl = indicator;
    }
  };

  const handleDragLeave = (e) => {
    // Only remove if we're actually leaving the item (not entering a child)
    if (!e.currentTarget.contains(e.relatedTarget)) {
      if (dragUiRef.current.dragOverEl === e.currentTarget) {
        clearDragUi();
      } else {
        e.currentTarget.classList.remove('drag-over');
      }
    }
  };

  const handleDrop = (e, targetPolicy) => {
    e.preventDefault();
    e.currentTarget.classList.remove('drag-over');
    clearDragUi();

    try {
      const draggedPolicy = JSON.parse(e.dataTransfer.getData('text/plain'));
      const draggedKey = policyKey(draggedPolicy);
      const targetKey = policyKey(targetPolicy);

      if (!draggedKey || !targetKey) return;
      if (draggedKey === targetKey) return; // Same policy

      setReservationPolicies((prev) => {
        const reorder = (array) => {
          const draggedIndex = array.findIndex(p => policyKey(p) === draggedKey);
          const targetIndex = array.findIndex(p => policyKey(p) === targetKey);

          if (draggedIndex === -1 || targetIndex === -1) return array;

          const newArray = [...array];
          const [draggedItem] = newArray.splice(draggedIndex, 1);
          newArray.splice(targetIndex, 0, draggedItem);
          return newArray;
        };

        // If showing only active policies, reorder only within active items (keep inactive positions stable)
        let reordered;
        if (showOnlyActive) {
          const active = prev.filter(p => p.IsActive);
          const reorderedActive = reorder(active);
          let activeIndex = 0;
          reordered = prev.map(p => (p.IsActive ? reorderedActive[activeIndex++] : p));
        } else {
          reordered = reorder(prev);
        }

        // Recalculate priorities for active policies only (0-based)
        let activePriority = 0;
        return reordered.map((policy) => {
          if (policy.IsActive) {
            const updated = { ...policy, Priority: activePriority };
            activePriority += 1;
            return updated;
          }
          return policy;
        });
      });
    } catch (error) {
      console.error('Error during drop:', error);
    }
  };

  const onCheckBoxChange = (e, data) => {
    setShowOnlyActive(data.checked);
  };

  const choosePolicyAndOpenModal = (chosenPolicy) => {
    setChosenReservationPolicy(chosenPolicy);
    setShowCancellationAndDepositPoliciesPopup(true);
  };

  const closeModalAndCleanChosenPolicy = useCallback((modalResult) => {

    setChosenReservationPolicy({});
    setShowCancellationAndDepositPoliciesPopup(false);
    if (modalResult) {
      console.log('closeModalAndCleanChosenPolicy modalResult: ', modalResult);
      const {
        oldReservationPolicyId,
        reservationPolicy
      } = modalResult

      setReservationPolicies((prev) => {
        const _prev = [...prev];
        const index = _prev.findIndex(r => r.Id === oldReservationPolicyId)
        _prev.splice(index, 1, reservationPolicy)

        return _prev;
      });
    }
  },
    []
  );

  return (
    <React.Fragment>
      <SideBar
        items={[{
          id: 1,
          cb: () => {
            window['PolicyController']('open', {
              HotelId: window.optima.user.CurrentHotel.Id,
            });
          },
          iconName: 'calendar times outline',
          toolTip: 'UpdatePolicy',
        }]}
        visible={true}
        onHide={() => setReservationPolicyUpdatePopupVisible(false)}
      >
        <Segment secondary>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <Button
              positive
              icon='plus'
              content={text('AddNewPolicy')}
              onClick={onAddNewPolicy}
            />
            <Checkbox
              label={text('ShowOnlyActive')}
              onChange={onCheckBoxChange}
              defaultChecked={true}
            />
          </div>
        </Segment>
        <Segment>
          {filteredReservationPolicies.some(p => p.Id) && (
            <div style={{
              textAlign: 'center',
              padding: '10px',
              background: '#f8f9fa',
              border: '1px dashed #dee2e6',
              borderRadius: '4px',
              marginBottom: '15px',
              fontSize: '14px',
              color: '#6c757d',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <i className="icon hand point up" style={{ marginRight: '8px', transform: 'rotate(180deg)' }} />
              <span>
                {text("DragAndDropToPrioritize")}
              </span>
            </div>
          )}
          <Item.Group divided>
            {filteredReservationPolicies.length ? (
              filteredReservationPolicies.map((currentPolicy) => (
                <Item
                  key={currentPolicy.Id || currentPolicy.InternalId}
                  className='reservation-policy-row'
                  draggable={Boolean(policyKey(currentPolicy))}
                  onDragStart={(e) => handleDragStart(e, currentPolicy)}
                  onDragEnd={handleDragEnd}
                  onDragOver={handleDragOver}
                  onDragEnter={(e) => handleDragEnter(e, currentPolicy)}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, currentPolicy)}
                  style={{
                    cursor: currentPolicy.Id ? 'move' : 'default',
                    opacity: currentPolicy.Id ? 1 : 0.8
                  }}
                >
                  <Item.Content>
                    <Item.Meta style={{ display: 'flex' }}>
                      <TextInput
                        style={{ flexGrow: '2' }}
                        transparent
                        fluid
                        size='large'
                        placeholder={text(policyPropertiesEnum.Name)}
                        maxLength={50}
                        onChange={(value) =>
                          onUpdatePolicy({
                            value,
                            currentPolicy,
                            property: policyPropertiesEnum.Name,
                          })
                        }
                        value={currentPolicy.Name}
                      />

                      <Popup
                        content={currentPolicy.Id ? message('ToSetThePriorityDdragThePolicy') : ''}
                        disabled={!currentPolicy.Id}
                        trigger={
                          <div>
                            <NumericInput
                              size='mini'
                              placeholder={text(policyPropertiesEnum.Priority)}
                              min={0}
                              digits={0}
                              nullable
                              onChange={(value) =>
                                onUpdatePolicy({
                                  value,
                                  currentPolicy,
                                  property: policyPropertiesEnum.Priority,
                                })
                              }
                              value={currentPolicy.Priority}
                              disabled={Boolean(currentPolicy.Id)}
                            // style={{ pointerEvents: currentPolicy.Id ? 'auto' : 'auto', width: '100%' }}
                            />
                          </div>
                        }
                        position="top center"
                        hoverable
                      />
                    </Item.Meta>
                    <Item.Description>
                      <TextArea
                        rows={3}
                        placeholder={text(policyPropertiesEnum.Description)}
                        disabled
                        style={{
                          minWidth: '-webkit-fill-available',
                          maxWidth: 'fit-content',
                          border: 'none',
                          outline: 'none',
                          fontSize: 'initial',
                        }}
                        value={buildAutoDescriptionForPolicy(currentPolicy, message, text, hotelTimezone)}
                      />
                    </Item.Description>
                    <Item.Extra >
                      <Checkbox
                        label={text(
                          currentPolicy.IsActive ? 'Active' : 'NotActive'
                        )}
                        checked={currentPolicy.IsActive}
                        onChange={(e, data) =>
                          onUpdatePolicy({
                            value: data.checked,
                            currentPolicy,
                            property: policyPropertiesEnum.IsActive,
                          })
                        }
                      />
                      <div className='action-buttons'>
                        <IconButton
                          floated="right"
                          icon="sticky note outline"
                          resetStyles={false}
                          basic
                          target="_blank"
                          content="Content"
                          href={`${endpoint}/PMS/Content/?EntityTypeId=${EntityType.ReservationPolicy}&EntityId=${currentPolicy.Id}`}
                          disabled={!currentPolicy.Id} />
                        <EditButton
                          floated="right"
                          basic
                          resetStyles={false}
                          onClick={() => choosePolicyAndOpenModal(currentPolicy)}
                          disabled={!currentPolicy.Id}
                        />
                      </div>
                    </Item.Extra>
                  </Item.Content>
                </Item>
              ))
            ) : (
              <div>{text('NoPoliciesToShow')}</div>
            )}
          </Item.Group>
        </Segment>
      </SideBar>

      {
        Object.keys(chosenReservationPolicy).length ? (
          <CancellationAndDepositPoliciesPopup
            startOpen={showCancellationAndDepositPoliciesPopup}
            onClose={closeModalAndCleanChosenPolicy}
            reservationPolicy={chosenReservationPolicy}
          />
        ) : null
      }
      <ReservationPolicyUpdatePopup controller='PolicyController' />
    </React.Fragment>
  );
};

export default ReservationPolicy;
